class CollectionSorting {
  constructor() {
    this.selectors = {
      sortSelect: '#SortBy',
      productGrid: '.suxnix-shop-product-main',
      productCount: '.woocommerce-result-count',
      loadingOverlay: '.collection-loading-overlay',
      priceSlider: '#slider-range',
      priceInput: '#amount',
      priceFilterForm: '.price_filter',
      priceFilterSubmit: '.price_filter .btn'
    };

    this.debounceTimer = null;
    this.init();
  }

  init() {
    // Initialize sort select handler
    const sortSelect = document.querySelector(this.selectors.sortSelect);
    if (sortSelect) {
      sortSelect.addEventListener('change', this.handleSort.bind(this));
    }

    // Initialize price slider with debounce
    this.initPriceSlider();
  }

  debounce(func, wait = 500) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => func(), wait);
  }

  initPriceSlider() {
    const slider = document.querySelector(this.selectors.priceSlider);
    const input = document.querySelector(this.selectors.priceInput);
    const form = document.querySelector(this.selectors.priceFilterForm);
    
    if (!slider || !input || !form || typeof jQuery === 'undefined') return;

    // Get price range from data attributes
    const minPrice = parseFloat(input.dataset.min) || 0;
    const maxPrice = parseFloat(input.dataset.max) || 1000;
    
    // Get current values from URL or use defaults
    const urlParams = new URLSearchParams(window.location.search);
    const currentMin = parseFloat(urlParams.get('price_min')) || minPrice;
    const currentMax = parseFloat(urlParams.get('price_max')) || maxPrice;

    // Initialize jQuery UI slider
    jQuery(slider).slider({
      range: true,
      min: minPrice,
      max: maxPrice,
      values: [currentMin, currentMax],
      create: () => {
        // Set initial input value with formatted money
        this.updatePriceDisplay(currentMin, currentMax);
      },
      slide: (event, ui) => {
        // Update input value with formatted money
        this.updatePriceDisplay(ui.values[0], ui.values[1]);
      },
      stop: (event, ui) => {
        // Use debounce on slider stop
        this.debounce(() => this.applyPriceFilter(ui.values[0], ui.values[1]));
      }
    });

    // Handle form submit
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const values = jQuery(slider).slider('values');
      this.applyPriceFilter(values[0], values[1]);
    });

    // Handle filter button click
    const filterBtn = form.querySelector(this.selectors.priceFilterSubmit);
    if (filterBtn) {
      filterBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const values = jQuery(slider).slider('values');
        this.applyPriceFilter(values[0], values[1]);
      });
    }
  }

  updatePriceDisplay(min, max) {
    const input = document.querySelector(this.selectors.priceInput);
    if (!input) return;

    const formattedMin = this.formatMoney(min);
    const formattedMax = this.formatMoney(max);
    input.value = `${formattedMin} - ${formattedMax}`;
  }

  formatMoney(amount) {
    // Get Shopify money format from theme settings
    const moneyFormat = window.theme?.moneyFormat || "{{ amount_with_comma_separator }}";
    
    try {
      // Convert to cents for Shopify's money formatter
      const cents = Math.round(amount * 100);
      
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }
      
      // Fallback to basic formatting if Shopify formatter is not available
      return new Intl.NumberFormat(window.Shopify?.locale || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(amount);
    } catch (error) {
      console.error('Error formatting money:', error);
      return `$${amount.toFixed(2)}`;
    }
  }

  async applyPriceFilter(minPrice, maxPrice) {
    const url = new URL(window.location.href);
    
    // Update URL parameters
    url.searchParams.set('price_min', minPrice);
    url.searchParams.set('price_max', maxPrice);

    this.showLoading();

    try {
      const response = await fetch(url.toString(), {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error('Network response was not ok');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update product grid
      const newProducts = doc.querySelector(this.selectors.productGrid);
      const currentProducts = document.querySelector(this.selectors.productGrid);
      
      if (newProducts && currentProducts) {
        currentProducts.innerHTML = newProducts.innerHTML;
        
        // Update URL without page reload
        window.history.pushState({}, '', url);

        // Reinitialize product functions
        this.reinitializeProductFunctions();
      }
    } catch (error) {
      console.error('Error applying price filter:', error);
    } finally {
      this.hideLoading();
    }
  }

  async handleSort(event) {
    event.preventDefault();
    const select = event.target;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);

    // Show loading state
    this.showLoading();

    try {
      const response = await fetch(url.toString(), {
        headers: {
          'Accept': 'text/html'
        }
      });

      if (!response.ok) throw new Error('Network response was not ok');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update product grid
      const newProductGrid = doc.querySelector(this.selectors.productGrid);
      const currentProductGrid = document.querySelector(this.selectors.productGrid);
      if (newProductGrid && currentProductGrid) {
        currentProductGrid.innerHTML = newProductGrid.innerHTML;
      }

      // Update product count
      const newCount = doc.querySelector(this.selectors.productCount);
      const currentCount = document.querySelector(this.selectors.productCount);
      if (newCount && currentCount) {
        currentCount.innerHTML = newCount.innerHTML;
      }

      // Update URL
      window.history.pushState({ path: url.toString() }, '', url.toString());

      // Re-initialize product functionality
      this.reinitializeProductFunctions();

    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.hideLoading();
    }
  }

  showLoading() {
    const grid = document.querySelector(this.selectors.productGrid);
    if (grid) {
      grid.style.opacity = '0.5';
      grid.style.pointerEvents = 'none';
    }
  }

  hideLoading() {
    const grid = document.querySelector(this.selectors.productGrid);
    if (grid) {
      grid.style.opacity = '1';
      grid.style.pointerEvents = 'auto';
    }
  }

  reinitializeProductFunctions() {
    // Re-initialize any product card specific functionality
    if (typeof initQuickView === 'function') {
      initQuickView();
    }
    if (typeof initAddToCart === 'function') {
      initAddToCart();
    }
    if (typeof initWishlist === 'function') {
      initWishlist();
    }
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  new CollectionSorting();
}); 